import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Settings2, CalendarDays, Flame, Plus, X, LifeBuoy, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { fmtShiftTime, untilText } from "@/components/PresenceChips";
import { OnCallRota } from "@/components/OnCallRota";
import { CoverageBoard, PersonWeekDialog } from "@/components/PresenceHistory";
import { ShiftTimesCard } from "@/components/ShiftTimesCard";
import { LateDigestCard } from "@/components/LateDigestCard";
import { WhoIsInCard } from "@/components/WhoIsInCard";
import { TeamFilter } from "@/components/TeamFilter";
import { AvatarNudge } from "@/components/AvatarNudge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";

function PtoDatePicker({ value, onChange, minDate, testid, placeholder = "Pick a date" }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testid}
          className={`w-full justify-start gap-2 font-normal ${!value ? "text-slate-400" : "text-slate-800"}`}>
          <CalendarDays className="h-4 w-4 text-slate-400" />
          {value ? format(selected, "MMM d, yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selected} defaultMonth={selected || minDate}
          disabled={minDate ? { before: minDate } : undefined}
          onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }} />
      </PopoverContent>
    </Popover>
  );
}
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const SYNC_ERROR_LABELS = {
  not_configured: "not configured — add the key under Settings → Platform → API Connections",
  unauthorized: "auth failed — key rejected (update it under Settings → Platform → API Connections)",
  token_expired: "API key expired — issue a new one and update it under Settings → Platform → API Connections",
  rate_limited: "rate limited",
  timeout: "timeout",
  http_error: "unreachable",
  unknown_error: "error",
};

const STATUS_META = {
  in: { label: "In · Working", short: "In", dot: "#16a34a", chip: "bg-green-50 text-green-700 border-green-200", ring: "ring-green-500/30" },
  break: { label: "On Break", short: "Break", dot: "#eab308", chip: "bg-yellow-50 text-yellow-700 border-yellow-200", ring: "ring-yellow-500/30" },
  lunch: { label: "Lunch", short: "Lunch", dot: "#f97316", chip: "bg-orange-50 text-orange-700 border-orange-200", ring: "ring-orange-500/30" },
  out: { label: "Out", short: "Out", dot: "#dc2626", chip: "bg-red-50 text-red-700 border-red-200", ring: "ring-red-500/30" },
  pto: { label: "PTO", short: "PTO", dot: "#64748b", chip: "bg-slate-100 text-slate-600 border-slate-200", ring: "ring-slate-400/30" },
};

const ORDER = ["in", "break", "lunch", "out", "pto"];

// For "Out": time/date since the member was last active (not Out/PTO).
function Light({ status }) {
  const m = STATUS_META[status] || STATUS_META.out;
  return (
    <span className="flex items-center gap-2.5">
      <span className={`relative flex h-4 w-4 items-center justify-center rounded-full ring-4 ${m.ring}`} style={{ backgroundColor: m.dot }}>
        {status === "in" && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ backgroundColor: m.dot }} />}
      </span>
      <span className="text-sm font-semibold text-slate-800">{m.label}</span>
    </span>
  );
}

function StatusDot({ s }) {
  return <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: STATUS_META[s].dot }} />;
}

export default function InOut() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [noteRow, setNoteRow] = useState(null); // {user_id, name, status, note}
  const [ptoRow, setPtoRow] = useState(null); // {user_id, name, note, pto_start, pto_end}
  const [upcomingRow, setUpcomingRow] = useState(null); // {user_id, name, events:[{start,end,note}]}
  const [humiOpen, setHumiOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null); // {user_id, name}
  const [onCallRow, setOnCallRow] = useState(null);   // row with on_call_handover
  const [humiUrl, setHumiUrl] = useState("");
  const [sdCount, setSdCount] = useState(0);
  // Iter 137 — multi-team filter (empty = all teams), remembered per browser.
  const [teamFilter, setTeamFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem("inout-teams") || "[]"); } catch { return []; }
  });
  const setTeams = (v) => {
    setTeamFilter(v);
    try { localStorage.setItem("inout-teams", JSON.stringify(v)); } catch { /* ignore */ }
  };
  const isStaff = user?.base_role === "staff";
  const canManageHumi = has("settings_platform.edit");

  const load = () => api.get("/inout").then((r) => setData(r.data)).catch((e) => {
    if (e.response?.status === 403) navigate("/dashboard");
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Iter 119e — keep the board live like the Who's In panel: refresh every 20s
  // and immediately when the tab regains focus.
  useEffect(() => {
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (has("service_desk.view")) api.get("/intercom/open-count").then((r) => setSdCount(r.data?.count || 0)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!isStaff) return null;

  const can = data?.can_manage;
  const firstNameOf = (r) => (r.name || "").trim().split(/\s+/)[0] || "";
  const teamsOf = (s) => (s.teams?.length ? s.teams : (s.team ? [s.team] : []));
  const teamOptions = Array.from(new Set((data?.staff || []).flatMap(teamsOf))).sort();
  const visibleStaff = (data?.staff || []).filter((s) => !teamFilter.length || teamsOf(s).some((t) => teamFilter.includes(t)));
  const counts = ORDER.reduce((acc, s) => { acc[s] = visibleStaff.filter((x) => x.status === s).length; return acc; }, {});
  const sortedStaff = [...visibleStaff].sort((a, b) => firstNameOf(a).toLowerCase().localeCompare(firstNameOf(b).toLowerCase()));
  const conflicts = visibleStaff.filter((s) => s.presence_conflict);

  const saveStatus = async (row, status) => {
    if (status === "pto") {
      setPtoRow({ user_id: row.user_id, name: row.name, note: row.note || "", pto_start: row.pto_start || "", pto_end: row.pto_end || "" });
      return;
    }
    try {
      await api.put(`/inout/${row.user_id}`, { status, note: row.note || "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update"); }
  };

  const savePto = async () => {
    try {
      await api.put(`/inout/${ptoRow.user_id}`, { status: "pto", note: ptoRow.note, pto_start: ptoRow.pto_start || null, pto_end: ptoRow.pto_end || null });
      toast.success("PTO set");
      setPtoRow(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
  };

  const saveNote = async () => {
    try {
      // Note-only endpoint — any staff member can leave one on a colleague.
      await api.put(`/inout/${noteRow.user_id}/note`, { note: noteRow.note });
      toast.success("Note saved");
      setNoteRow(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
  };

  const openUpcoming = (row) => {
    const evs = (row.upcoming_pto || []).slice(0, 3).map((e) => ({ start: e.start || "", end: e.end || "", note: e.note || "" }));
    setUpcomingRow({ user_id: row.user_id, name: row.name, events: evs });
  };

  const saveUpcoming = async () => {
    const events = (upcomingRow.events || []).filter((e) => e.start);
    try {
      await api.put(`/inout/${upcomingRow.user_id}`, { upcoming_pto: events });
      toast.success("Upcoming PTO saved");
      setUpcomingRow(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
  };

  const saveHumi = async () => {
    try {
      await api.put("/settings", { humi_url: humiUrl.trim() });
      toast.success("Humi link saved");
      setHumiOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
  };


  return (
    <div className="space-y-5" data-testid="inout-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">In Out Board</h1>
          <p className="mt-1 text-sm text-slate-500">Live staff availability — who's in, out, on break or PTO.</p>
        </div>
        <div className="flex items-center gap-2">
          {teamFilter.length > 0 && (
            <span className="text-xs text-slate-500" data-testid="inout-team-count">
              Showing {visibleStaff.length} of {(data?.staff || []).length}
            </span>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="inout-all-teams"
            onClick={() => navigate("/teams")}>
            All teams
          </Button>
          {teamFilter.length === 1 && (
            <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="inout-open-team-page"
              onClick={() => navigate(`/teams/${encodeURIComponent(teamFilter[0])}`)}>
              Open {teamFilter[0]} page
            </Button>
          )}
          <TeamFilter teams={teamOptions} value={teamFilter} onChange={setTeams}
            countFor={(t) => (data?.staff || []).filter((s) => teamsOf(s).includes(t)).length}
            testid="inout-team-filter" />
        </div>
      </div>

      <AvatarNudge testid="inout-avatar-nudge" />

      {/* Summary */}
      <div className="flex flex-wrap gap-2" data-testid="inout-summary">
        {ORDER.map((s) => (
          <div key={s} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${STATUS_META[s].chip}`} data-testid={`summary-${s}`}>
            <StatusDot s={s} />
            {STATUS_META[s].short} <span className="font-bold">{counts[s] || 0}</span>
          </div>
        ))}
        {conflicts.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="presence-conflicts-chip"
                className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100">
                <AlertTriangle className="h-3.5 w-3.5" /> {conflicts.length} conflict{conflicts.length === 1 ? "" : "s"}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-96 p-3" data-testid="presence-conflicts-list">
              <p className="mb-2 text-xs text-slate-500">
                Teams and Intercom disagree — we left these people alone instead of marking them Out.
              </p>
              <ul className="space-y-1.5">
                {conflicts.map((c) => (
                  <li key={c.user_id} className="flex items-center gap-2 text-xs" data-testid={`presence-conflict-row-${c.user_id}`}>
                    <Avatar name={c.name} src={c.avatar_url} size="sm" fallback="mystery" />
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{c.name}</span>
                    <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">Teams {c.teams_status || "—"}</span>
                    <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800">Intercom {c.intercom_status || "—"}</span>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        )}
        {data?.on_call_sync_error && (
          <div data-testid="oncall-sync-error"
            title={`On-call data may be stale — incident.io ${SYNC_ERROR_LABELS[data.on_call_sync_error] || data.on_call_sync_error}`}
            className="ml-auto flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5" /> On-call sync: {SYNC_ERROR_LABELS[data.on_call_sync_error] || "unavailable"}
          </div>
        )}
      </div>

      {/* Iter 134 — staff directory health: people who should be on this board
          but are hidden because their role no longer exists. */}
      {(data?.hidden_staff || []).length > 0 && (
        <div data-testid="inout-hidden-staff-alert"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {data.hidden_staff.length} staff member{data.hidden_staff.length === 1 ? " is" : "s are"} hidden from this board
              </p>
              <ul className="mt-1.5 space-y-1">
                {data.hidden_staff.map((h) => (
                  <li key={h.user_id} className="text-xs text-amber-800 dark:text-amber-300" data-testid={`inout-hidden-staff-${h.user_id}`}>
                    <span className="font-semibold">{h.name || h.email}</span>
                    {h.team ? <span className="text-amber-700/80 dark:text-amber-400/80"> · {h.team}</span> : null}
                    <span className="text-amber-700/80 dark:text-amber-400/80"> — {h.detail}</span>
                  </li>
                ))}
              </ul>
              {has("users.edit") && (
                <Button variant="outline" size="sm" className="mt-2 h-7 border-amber-300 bg-white text-xs text-amber-900 hover:bg-amber-100"
                  data-testid="inout-hidden-staff-fix"
                  onClick={() => navigate(`/settings?tab=staff&q=${encodeURIComponent(data.hidden_staff[0].email || "")}`)}>
                  <Settings2 className="h-3.5 w-3.5" /> Fix in Admin &amp; Staff
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legend — traffic-light dots (live Teams presence) + app abbreviations */}
      <div data-testid="inout-legend" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
        <span className="font-semibold uppercase tracking-wide text-foreground/70">Legend</span>
        <span className="text-muted-foreground/50">dot = live Teams presence:</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#16a34a" }} /> Available</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#eab308" }} /> Away / BRB</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#dc2626" }} /> Busy / Meeting / DND</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#cbd5e1" }} /> Offline</span>
        <span className="text-muted-foreground/40">|</span>
        <span className="text-muted-foreground/50">card outline = live Teams presence:</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-emerald-500" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-orange-500" /> Away</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-red-500" /> Offline / Busy</span>
        <span className="text-muted-foreground/40">|</span>
        <span><b className="text-foreground/70">T</b> Teams</span>
        <span><b className="text-foreground/70">HD</b> Help Desk</span>
        <span><b className="text-foreground/70">OC</b> On-call</span>
      </div>

      {/* Board — the Who's-in card view (moved here from the slide-out in
          Iter 133), single combined grid sorted by first name. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="inout-grid">
        {sortedStaff.map((row) => {
          const editable = row.user_id === data?.me || can;
          return (
            <WhoIsInCard
              key={row.user_id}
              row={row}
              me={{ user_id: data?.me }}
              appId={data?.intercom_app_id}
              prefix="inout"
              large
              onAvatarUpdated={load}
              onHistory={setHistoryRow}
              onStatusChange={editable ? saveStatus : undefined}
              onNote={(r) => setNoteRow({ user_id: r.user_id, name: r.name, status: r.status,
                note: r.note || "", note_by: r.note_by || "" })}
              onUpcoming={editable ? openUpcoming : undefined}
            />
          );
        })}
        {data && (data.staff || []).length === 0 && (
          <div className="col-span-full rounded-xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">No staff members found.</div>
        )}
      </div>

      {/* Shift times — earliest/latest seen per person (MDT) */}
      <ShiftTimesCard />

      {/* Repeat late-start digest to managers (staff with inout.manage) */}
      {can && <LateDigestCard />}


      {/* Coverage — 7-day in/out patterns per person */}
      <CoverageBoard />

      {/* On-call rota + handover (collapsible, bottom of the board) */}
      <OnCallRota rota={data?.on_call_rota || []} syncError={data?.rota_sync_error} />

      {/* Presence conflict trail */}

      {/* Note dialog */}
      <Dialog open={!!noteRow} onOpenChange={(o) => !o && setNoteRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{noteRow?.user_id === data?.me ? "Your note" : `Note for ${noteRow?.name}`}</DialogTitle>
            <DialogDescription>
              {noteRow?.user_id === data?.me
                ? 'Add a short note so the team has context (e.g. "Back at 1pm").'
                : `Anyone can leave a note for a colleague — e.g. "back on Sept 30". It shows on their card with your name against it.${noteRow?.note_by ? ` Currently added by ${noteRow.note_by}.` : ""}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Textarea data-testid="inout-note-input" rows={2} value={noteRow?.note || ""} maxLength={200}
              onChange={(e) => setNoteRow((p) => ({ ...p, note: e.target.value }))}
              placeholder="e.g. Back at 1pm, reachable on cell…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteRow(null)}>Cancel</Button>
            <Button data-testid="inout-note-save-button" onClick={saveNote} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PTO date range dialog */}
      <Dialog open={!!ptoRow} onOpenChange={(o) => !o && setPtoRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ptoRow?.user_id === data?.me ? "Set your PTO" : `Set PTO for ${ptoRow?.name}`}</DialogTitle>
            <DialogDescription>Choose the date range you'll be away so the team knows when you're back.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>From</Label>
                <PtoDatePicker testid="pto-start" value={ptoRow?.pto_start || ""} placeholder="yyyy-mm-dd"
                  onChange={(v) => setPtoRow((p) => ({ ...p, pto_start: v, pto_end: p.pto_end && p.pto_end < v ? "" : p.pto_end }))} /></div>
              <div className="space-y-1.5"><Label>To</Label>
                <PtoDatePicker testid="pto-end" value={ptoRow?.pto_end || ""} placeholder="yyyy-mm-dd"
                  minDate={ptoRow?.pto_start ? parseISO(ptoRow.pto_start) : undefined}
                  onChange={(v) => setPtoRow((p) => ({ ...p, pto_end: v }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Note (optional)</Label>
              <Textarea data-testid="pto-note-input" rows={2} value={ptoRow?.note || ""} maxLength={200}
                onChange={(e) => setPtoRow((p) => ({ ...p, note: e.target.value }))} placeholder="e.g. Family vacation, limited email" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPtoRow(null)}>Cancel</Button>
            <Button data-testid="pto-save-button" onClick={savePto} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save PTO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upcoming PTO editor dialog */}
      <Dialog open={!!upcomingRow} onOpenChange={(o) => !o && setUpcomingRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{upcomingRow?.user_id === data?.me ? "Your upcoming PTO" : `Upcoming PTO for ${upcomingRow?.name}`}</DialogTitle>
            <DialogDescription>List up to 3 future time-off events so the team can plan ahead. Shown on the In/Out card.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(upcomingRow?.events || []).map((ev, i) => (
              <div key={i} className="rounded-lg border border-border p-3" data-testid={`upcoming-event-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Event {i + 1}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`upcoming-remove-${i}`}
                    onClick={() => setUpcomingRow((p) => ({ ...p, events: p.events.filter((_, j) => j !== i) }))}>
                    <X className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>From</Label>
                    <PtoDatePicker testid={`upcoming-start-${i}`} value={ev.start || ""} placeholder="yyyy-mm-dd"
                      onChange={(v) => setUpcomingRow((p) => ({ ...p, events: p.events.map((e, j) => j === i ? { ...e, start: v, end: e.end && e.end < v ? "" : e.end } : e) }))} /></div>
                  <div className="space-y-1.5"><Label>To</Label>
                    <PtoDatePicker testid={`upcoming-end-${i}`} value={ev.end || ""} placeholder="yyyy-mm-dd"
                      minDate={ev.start ? parseISO(ev.start) : undefined}
                      onChange={(v) => setUpcomingRow((p) => ({ ...p, events: p.events.map((e, j) => j === i ? { ...e, end: v } : e) }))} /></div>
                </div>
                <div className="mt-2 space-y-1.5"><Label>Note (optional)</Label>
                  <Input data-testid={`upcoming-note-${i}`} value={ev.note || ""} maxLength={120} placeholder="e.g. Conference, vacation"
                    onChange={(e) => setUpcomingRow((p) => ({ ...p, events: p.events.map((x, j) => j === i ? { ...x, note: e.target.value } : x) }))} /></div>
              </div>
            ))}
            {(upcomingRow?.events || []).length < 3 && (
              <Button variant="outline" size="sm" data-testid="upcoming-add" onClick={() => setUpcomingRow((p) => ({ ...p, events: [...(p.events || []), { start: "", end: "", note: "" }] }))}>
                <Plus className="h-4 w-4" /> Add PTO event
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpcomingRow(null)}>Cancel</Button>
            <Button data-testid="upcoming-save-button" onClick={saveUpcoming} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Humi link dialog */}
      <Dialog open={humiOpen} onOpenChange={setHumiOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Humi PTO link</DialogTitle>
            <DialogDescription>Paste the Humi app URL where staff log in to request time off. This persists for everyone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Humi URL</Label>
            <Input data-testid="humi-url-input" value={humiUrl} onChange={(e) => setHumiUrl(e.target.value)} placeholder="https://app.humi.ca" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHumiOpen(false)}>Cancel</Button>
            <Button data-testid="humi-save-button" onClick={saveHumi} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* On-call detail dialog */}
      <Dialog open={!!onCallRow} onOpenChange={(o) => !o && setOnCallRow(null)}>
        <DialogContent data-testid="oncall-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-orange-500" fill="currentColor" />
              {onCallRow?.name} is on call
            </DialogTitle>
            <DialogDescription>
              {onCallRow?.on_call_handover?.schedule
                ? `${onCallRow.on_call_handover.schedule} · via incident.io`
                : "via incident.io"}
            </DialogDescription>
          </DialogHeader>
          {onCallRow?.on_call_handover ? (
            <div className="space-y-3 text-sm" data-testid="oncall-detail-body">
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-500/30 dark:bg-orange-500/10">
                <div className="text-[10px] font-bold uppercase tracking-wide text-orange-700 dark:text-orange-300">Current shift</div>
                <div className="mt-1 font-semibold text-slate-800 dark:text-slate-100">{onCallRow.name}</div>
                {onCallRow.on_call_handover.started_at && (
                  <div className="text-xs text-slate-600 dark:text-slate-300">Started {fmtShiftTime(onCallRow.on_call_handover.started_at)}</div>
                )}
                {onCallRow.on_call_handover.ends_at && (
                  <div className="text-xs text-slate-600 dark:text-slate-300" data-testid="oncall-detail-ends">
                    Hands over {fmtShiftTime(onCallRow.on_call_handover.ends_at)} · <span className="font-semibold">{untilText(onCallRow.on_call_handover.ends_at)}</span>
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 dark:border-sky-500/30 dark:bg-sky-500/10">
                <div className="text-[10px] font-bold uppercase tracking-wide text-sky-700 dark:text-sky-300">Up next</div>
                {onCallRow.on_call_handover.next_name ? (
                  <>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-100" data-testid="oncall-detail-next">{onCallRow.on_call_handover.next_name}</div>
                    {onCallRow.on_call_handover.next_starts_at && (
                      <div className="text-xs text-slate-600 dark:text-slate-300">
                        Takes over {fmtShiftTime(onCallRow.on_call_handover.next_starts_at)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">No one is scheduled after this shift.</div>
                )}
              </div>
              {onCallRow.on_call_handover.timezone && (
                <p className="text-[11px] text-slate-400">Schedule timezone: {onCallRow.on_call_handover.timezone} · times shown in your local time</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="oncall-detail-empty">
              This person is on call, but we couldn't read their shift window from incident.io.
            </p>
          )}
          <DialogFooter>
            {onCallRow?.on_call_handover?.permalink && (
              <Button variant="outline" data-testid="oncall-detail-open-schedule"
                onClick={() => window.open(onCallRow.on_call_handover.permalink, "_blank", "noreferrer")}>
                <ExternalLink className="h-4 w-4" /> Open schedule
              </Button>
            )}
            <Button onClick={() => setOnCallRow(null)} className="bg-primary text-primary-foreground hover:bg-primary/90">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PersonWeekDialog open={!!historyRow} onOpenChange={(o) => !o && setHistoryRow(null)}
        userId={historyRow?.user_id} name={historyRow?.name} />
    </div>
  );
}
